import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { shareMessage, shortUrl } from '@/shared/referralLink';
import { REFERRAL_ART_SIZE, loadReferralArt } from '@/shared/referralArt';
import { cn } from '@/lib/utils';

// Colours baked into the exported image (the canvas cannot read CSS tokens). They match the
// Ghost Cable dark theme and the artwork, so the card looks the same in either site theme.
const INK = '#f5f3fa';
const MUTED = '#bebacc';
const ECTO = '#8cdecf';
const ECTO_FILL = 'rgba(140, 222, 207, 0.12)';

const SANS = "Rubik, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the card artwork.'));
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Biggest font size (down to `min`) at which `text` fits in `maxWidth`. */
function fitFont(ctx, text, weight, start, min, maxWidth) {
  let size = start;
  for (; size > min; size -= 1) {
    ctx.font = `${weight} ${size}px ${SANS}`;
    if (ctx.measureText(text).width <= maxWidth) break;
  }
  ctx.font = `${weight} ${size}px ${SANS}`;
  return size;
}

/** Draw the share card for one buyer and return it as a PNG blob. */
export async function drawReferralCard({ username, url }) {
  const artUrl = await loadReferralArt();
  const { width: W, height: H } = REFERRAL_ART_SIZE;
  try {
    await Promise.all([
      document.fonts?.load(`400 28px Rubik`),
      document.fonts?.load(`600 36px Rubik`),
      document.fonts?.load(`700 28px Rubik`),
    ]);
  } catch {
    // system font fallback is fine
  }
  const art = await loadImage(artUrl);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(art, 0, 0, W, H);

  // The link, in a glowing pill.
  const left = 64;
  const right = W - 64;
  const top = 1140;
  const pillH = 86;
  roundRect(ctx, left, top, right - left, pillH, pillH / 2);
  ctx.fillStyle = ECTO_FILL;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = ECTO;
  ctx.shadowColor = 'rgba(140, 222, 207, 0.55)';
  ctx.shadowBlur = 18;
  ctx.stroke();
  ctx.shadowBlur = 0;

  const link = shortUrl(url);
  ctx.textBaseline = 'middle';
  ctx.fillStyle = INK;
  fitFont(ctx, link, 600, 36, 18, right - left - 72);
  ctx.fillText(link, left + 36, top + pillH / 2 + 1);

  // "Or type my username NAME at checkout."
  const y = 1278;
  let x = 66;
  const parts = [
    { text: 'Or type my username ', weight: 400, color: MUTED },
    { text: username, weight: 700, color: ECTO },
    { text: ' at checkout.', weight: 400, color: MUTED },
  ];
  let size = 28;
  const widthAt = (s) =>
    parts.reduce((w, p) => {
      ctx.font = `${p.weight} ${s}px ${SANS}`;
      return w + ctx.measureText(p.text).width;
    }, 0);
  while (size > 16 && widthAt(size) > right - 66) size -= 1;
  for (const p of parts) {
    ctx.font = `${p.weight} ${size}px ${SANS}`;
    ctx.fillStyle = p.color;
    ctx.fillText(p.text, x, y);
    x += ctx.measureText(p.text).width;
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not build the image.'))), 'image/png');
  });
}

/**
 * The buyer's shareable picture: Ghost Cable art with their link and username on it,
 * with download and share buttons.
 */
export default function ReferralShareCard({ username, url, className }) {
  const [blob, setBlob] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const fileName = `ghost-cable-${username}.png`;

  useEffect(() => {
    if (!username || !url) return undefined;
    let live = true;
    let objectUrl = '';
    setError('');
    setBlob(null);
    setPreview('');
    drawReferralCard({ username, url })
      .then((b) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(b);
        setBlob(b);
        setPreview(objectUrl);
      })
      .catch((e) => {
        if (live) setError(e?.message || 'Could not build your image.');
      });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [username, url, attempt]);

  const file = blob ? new File([blob], fileName, { type: 'image/png' }) : null;
  const canShareFile = !!(file && navigator.canShare?.({ files: [file] }));

  async function shareImage() {
    if (!file) return;
    setSharing(true);
    try {
      await navigator.share({ files: [file], text: shareMessage(username, url), title: 'Ghost Cable' });
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Sharing did not work here. Download the image and post it instead.');
    } finally {
      setSharing(false);
    }
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {preview ? (
          <img
            src={preview}
            width={1080}
            height={1350}
            alt={`Ghost Cable share card: a ghost watching TV, with your link ${shortUrl(url)} and username ${username}`}
            className="block h-auto w-full"
          />
        ) : error ? (
          <div className="flex aspect-[4/5] flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
            <ApperIcon name="ImageOff" size={28} />
            {error}
            <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
              <ApperIcon name="RefreshCw" size={16} />
              Try again
            </Button>
          </div>
        ) : (
          <Skeleton className="aspect-[4/5] w-full rounded-none" />
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {preview ? (
          <Button asChild className="flex-1 sm:flex-none">
            <a href={preview} download={fileName}>
              <ApperIcon name="Download" size={16} />
              Download image
            </a>
          </Button>
        ) : error ? null : (
          <span className="inline-flex items-center gap-2 py-2 text-sm text-muted-foreground" role="status">
            <ApperIcon name="Loader2" size={16} className="animate-spin" />
            Building your picture…
          </span>
        )}
        {canShareFile && (
          <Button variant="outline" onClick={shareImage} disabled={sharing} className="flex-1 sm:flex-none">
            <ApperIcon name={sharing ? 'Loader2' : 'Share2'} size={16} className={sharing ? 'animate-spin' : undefined} />
            Share image
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Post it to your story, a group chat or anywhere friends hang out. The link and username on it are yours.
      </p>
    </div>
  );
}
